// The loader: create a host element with an open shadow root, paint the
// launcher straight away, fetch the business's widget config, and load the
// React panel (widget-panel.js) the first time the visitor hovers, focuses or
// opens the launcher (at once in studio preview). Everything CSS- and
// DOM-related stays inside the shadow tree so host-site stylesheets can't
// bleed in and our DOM queries can't collide with theirs.
//
// One widget per page: `mount()` is a no-op while one is mounted, and bails
// if another copy of the script already put its host element in the page.

import { animateLauncherIn, animatePanelClose, animatePanelOpen } from './animations'
import {
  fetchWidgetRemoteConfig,
  resolvePanelUrl,
  resolveServerUrl,
  resolveWidget,
  type AevoEchoConfig,
  type WidgetRemoteConfig,
} from './config'
import type { FrameRefs } from './frame'
import { LAUNCHER_ICONS } from './icons'
import {
  attachLauncherPosition,
  finalizePanelOpenLayout,
  preparePanelOpenLayout,
  restoreCollapsedLauncherLayout,
} from './launcher-position'
import { readHostConfig } from './legacy'
import { LOADER_STYLES } from './loader-styles'
import { buildThemeCss, FONT_STYLESHEETS } from './palette'
import type { PanelHandle, PanelModule } from './panel-contract'
import { loadPanelScript } from './panel-loader'
import { attachPreview, postToStudio, type PreviewController } from './preview'
import { resolvePreviewOrigins, type PreviewInboundMessage, type PreviewPart } from './preview-protocol'
import { attachResize, type ResizeController } from './resize'
import { createStore, type WidgetState, type WidgetStore } from './store'
import { normalizeWidgetConfig, type WidgetConfig } from './widget-config'

const HOST_ELEMENT_ID = 'aevo-echo-host'
const CONFIG_CACHE_PREFIX = 'aevo_widget_config:'
/** How long a first visit waits for the widget config before painting defaults. */
const CONFIG_WAIT_MS = 1500

export const EVENT_OPEN = 'aevo-echo:open' as const
export const EVENT_CLOSE = 'aevo-echo:close' as const

export interface MountOptions {
  /** Put the widget inside this element instead of `document.body`. */
  target?: HTMLElement
  /** A WidgetConfig subset applied over the served config. */
  config?: unknown
  /** Studio preview mode (see `preview-protocol.ts`). */
  preview?: boolean
}

export interface MountDeps {
  loadPanel: (url: string) => Promise<PanelModule>
  fetchRemote: (serverUrl: string, widgetId: string | undefined) => Promise<WidgetRemoteConfig>
}

export interface MountedWidget {
  hostEl: HTMLElement
  shadowRoot: ShadowRoot
  refs: FrameRefs
  store: WidgetStore
  open: () => void
  close: () => void
  toggle: () => void
  isOpen: () => boolean
  update: (patch: unknown) => void
  destroy: () => void
  /** Resolves once the panel has rendered (tests, preview). */
  panelReady: () => Promise<void>
}

let mounted: MountedWidget | null = null

/** Current mount, if any — for `isOpen()` without eager-mounting. */
export function getMounted(): MountedWidget | null {
  return mounted
}

function dispatchWidgetEvent(name: typeof EVENT_OPEN | typeof EVENT_CLOSE): void {
  if (typeof document === 'undefined') return
  document.dispatchEvent(new CustomEvent(name, { bubbles: true }))
}

const PANEL_FOCUSABLE_SELECTOR =
  'button:not([disabled]), textarea:not([disabled]), input:not([disabled]), a[href], [tabindex]:not([tabindex="-1"])'

function listPanelFocusables(panel: HTMLElement): HTMLElement[] {
  return Array.from(panel.querySelectorAll<HTMLElement>(PANEL_FOCUSABLE_SELECTOR)).filter((el) => {
    const cs = globalThis.getComputedStyle(el)
    return cs.display !== 'none' && cs.visibility !== 'hidden'
  })
}

function readCachedConfig(widgetId: string | undefined): unknown | null {
  if (!widgetId) return null
  try {
    const raw = localStorage.getItem(CONFIG_CACHE_PREFIX + widgetId)
    return raw ? JSON.parse(raw) : null
  } catch {
    return null
  }
}

/** Remember the published config for the next page view (null forgets it). */
function writeCachedConfig(widgetId: string, config: unknown): void {
  try {
    if (config) localStorage.setItem(CONFIG_CACHE_PREFIX + widgetId, JSON.stringify(config))
    else localStorage.removeItem(CONFIG_CACHE_PREFIX + widgetId)
  } catch {
    /* storage unavailable */
  }
}

/** The transform origin for open/close: the launcher's corner. */
function panelOrigin(position: string): string {
  const [v, h] = position.split('-') as [string, string]
  const vertical = v === 'top' ? 'top' : v === 'center' ? 'center' : 'bottom'
  const horizontal = h === 'left' ? 'left' : h === 'center' ? 'center' : 'right'
  return `${vertical} ${horizontal}`
}

function ensureFontStylesheet(font: WidgetConfig['appearance']['font']): void {
  const href = FONT_STYLESHEETS[font]
  if (!href || typeof document === 'undefined') return
  // Fonts declared inside a shadow root are ignored by some browsers, so the
  // stylesheet goes in the page head, once per font.
  const id = `aevo-echo-font-${font}`
  if (document.getElementById(id)) return
  const link = document.createElement('link')
  link.id = id
  link.rel = 'stylesheet'
  link.href = href
  document.head.appendChild(link)
}

function renderLauncher(launcher: HTMLButtonElement, config: WidgetConfig): void {
  const { launcher: opts } = config.appearance
  const avatar = config.content.identity.avatarUrl
  launcher.classList.toggle('is-pill', opts.shape === 'pill')
  launcher.replaceChildren()
  if (opts.icon === 'avatar' && avatar) {
    const img = document.createElement('img')
    img.src = avatar
    img.alt = ''
    launcher.appendChild(img)
  } else {
    const icon = opts.icon === 'avatar' ? 'message' : opts.icon
    launcher.insertAdjacentHTML('beforeend', LAUNCHER_ICONS[icon])
  }
  if (opts.shape === 'pill' && opts.label) {
    const label = document.createElement('span')
    label.textContent = opts.label
    launcher.appendChild(label)
  }
  launcher.setAttribute('aria-label', opts.shape === 'pill' && opts.label ? opts.label : 'Open chat')
}

export function mount(options: MountOptions = {}, deps: Partial<MountDeps> = {}): MountedWidget | null {
  if (mounted) return mounted
  if (typeof document === 'undefined') return null
  if (document.getElementById(HOST_ELEMENT_ID)) return null

  const loadPanel = deps.loadPanel ?? loadPanelScript
  const fetchRemote = deps.fetchRemote ?? fetchWidgetRemoteConfig
  const host: AevoEchoConfig = readHostConfig() ?? {}
  const preview = options.preview ?? host.preview === true
  const inline = options.config ?? host.config
  const widgetId = typeof host.widgetId === 'string' && host.widgetId ? host.widgetId : undefined
  const serverUrl = resolveServerUrl(host)
  const version = __WIDGET_VERSION__
  const panelUrl = resolvePanelUrl(serverUrl, `${version}-${__PANEL_HASH__}`, host)

  // ---- DOM
  const hostEl = document.createElement('div')
  hostEl.id = HOST_ELEMENT_ID
  const shadowRoot = hostEl.attachShadow({ mode: 'open' })

  const baseStyle = document.createElement('style')
  baseStyle.textContent = LOADER_STYLES
  const themeStyle = document.createElement('style')
  shadowRoot.append(baseStyle, themeStyle)

  const root = document.createElement('div')
  root.className = 'se-root is-collapsed'
  if (preview) root.classList.add('is-preview')
  if (options.target) root.classList.add('is-contained')

  const launcher = document.createElement('button')
  launcher.type = 'button'
  launcher.className = 'se-launcher'
  launcher.dataset.sePart = 'launcher'
  launcher.setAttribute('aria-expanded', 'false')

  const panel = document.createElement('div')
  panel.className = 'se-panel'
  panel.setAttribute('role', 'dialog')
  panel.setAttribute('aria-label', 'Chat')
  panel.tabIndex = -1
  // React owns this element (createRoot clears whatever is in it); the resize
  // handle lives beside it in the panel frame.
  const panelBody = document.createElement('div')
  panelBody.className = 'se-panel-body'
  const spinner = document.createElement('div')
  spinner.className = 'se-panel-loading'
  spinner.setAttribute('aria-hidden', 'true')
  panelBody.appendChild(spinner)
  panel.appendChild(panelBody)

  // Popovers and hover cards render here: inside the shadow root (so they get
  // our styles) but outside the panel's `overflow: hidden`.
  const portalContainer = document.createElement('div')
  portalContainer.className = 'se-portal'

  root.append(launcher, panel)
  shadowRoot.append(root, portalContainer)
  const refs: FrameRefs = { root, launcher, panel }

  // ---- State. First paint uses the cached config for this widget when we
  // have one, else the inline/legacy config; Echo's answer replaces it.
  const cached = readCachedConfig(widgetId)
  const initial = resolveWidget(
    { status: 'ok', widgetId: widgetId ?? null, config: cached, announcement: null, suggestedPrompts: [], away: false },
    host,
    inline,
  )
  const store = createStore({
    config: initial.config,
    legacy: initial.legacy,
    announcement: initial.announcement,
    away: initial.away,
    conversationTtlMs: initial.conversationTtlMs,
    widgetId: widgetId ?? null,
    open: false,
    preview: preview ? { state: 'closed', highlight: null, demoRun: 0 } : null,
  })

  // ---- Applying a config to the loader's DOM
  let disposePosition: () => void = () => {}
  let resize: ResizeController = { dispose: () => {}, syncHandleSide: () => {} }
  let applied: { position: string; draggable: boolean; corner: string } | null = null

  const applyConfig = (state: WidgetState): void => {
    const { config } = state
    themeStyle.textContent = buildThemeCss(config, { legacy: state.legacy })
    root.dataset.theme = config.appearance.theme
    root.dataset.density = config.appearance.density
    portalContainer.dataset.theme = config.appearance.theme
    ensureFontStylesheet(config.appearance.font)
    renderLauncher(launcher, config)

    const { position, draggable, resizeCorner } = config.appearance.placement
    const next = { position, draggable: draggable && !preview, corner: preview ? 'none' : resizeCorner }
    if (!applied || applied.position !== next.position || applied.draggable !== next.draggable) {
      disposePosition()
      disposePosition = attachLauncherPosition(refs, {
        position,
        draggable: next.draggable,
        persist: !preview,
      })
    }
    if (!applied || applied.corner !== next.corner) {
      resize.dispose()
      resize = attachResize(refs, next.corner as typeof resizeCorner)
    }
    applied = next
  }
  applyConfig(store.get())
  let lastConfig = store.get().config
  let lastLegacy = store.get().legacy
  const unsubscribeConfig = store.subscribe(() => {
    const state = store.get()
    if (state.config === lastConfig && state.legacy === lastLegacy) return
    lastConfig = state.config
    lastLegacy = state.legacy
    applyConfig(state)
  })

  // ---- The panel
  let panelHandle: PanelHandle | null = null
  let panelPromise: Promise<void> | null = null
  let destroyed = false
  let preview_: PreviewController | null = null

  const ensurePanel = (): Promise<void> => {
    if (panelPromise) return panelPromise
    panelPromise = loadPanel(panelUrl)
      .then((mod) => {
        if (destroyed) return
        spinner.remove()
        panelHandle = mod.render({
          store,
          shadowRoot,
          container: panelBody,
          portalContainer,
          serverUrl,
          preview,
          version,
          close: () => controller.close(),
          selectPart: (part: PreviewPart, index?: number) =>
            postToStudio(previewOrigins, {
              type: 'aevo-echo:preview-select',
              part,
              ...(index !== undefined ? { index } : {}),
            }),
        })
        preview_?.ready(version)
      })
      .catch((err: unknown) => {
        panelPromise = null
        console.error('[aevo-echo] panel failed to load', err)
      })
    return panelPromise
  }

  // ---- Open / close
  let isClosing = false
  const isOpen = (): boolean => !root.classList.contains('is-collapsed')
  const setCollapsed = (collapsed: boolean): void => {
    root.classList.toggle('is-collapsed', collapsed)
    launcher.setAttribute('aria-expanded', String(!collapsed))
  }

  const controller = {
    open(): void {
      void ensurePanel()
      if (isOpen()) return
      preparePanelOpenLayout(refs)
      setCollapsed(false)
      animatePanelOpen(panel, panelOrigin(store.get().config.appearance.placement.position))
      store.set({ open: true })
      dispatchWidgetEvent(EVENT_OPEN)
      requestAnimationFrame(() => {
        finalizePanelOpenLayout(refs)
        resize.syncHandleSide()
      })
    },
    close(): void {
      if (!isOpen() || isClosing) return
      isClosing = true
      animatePanelClose(panel, panelOrigin(store.get().config.appearance.placement.position), () => {
        isClosing = false
        if (destroyed) return
        setCollapsed(true)
        restoreCollapsedLauncherLayout(refs)
        animateLauncherIn(launcher)
        store.set({ open: false })
        dispatchWidgetEvent(EVENT_CLOSE)
      })
    },
    toggle(): void {
      if (isOpen()) controller.close()
      else controller.open()
    },
  }

  const preload = (): void => {
    void ensurePanel()
  }
  launcher.addEventListener('pointerenter', preload, { once: true })
  launcher.addEventListener('focus', preload, { once: true })
  launcher.addEventListener('click', () => controller.open())

  const onDocumentKeydown = (e: KeyboardEvent): void => {
    if (e.key !== 'Escape' || !isOpen() || preview) return
    // An open menu or hover card closes first; the panel on the next Escape.
    if (portalContainer.childElementCount > 0) return
    controller.close()
  }
  document.addEventListener('keydown', onDocumentKeydown)

  const onPanelKeydown = (e: KeyboardEvent): void => {
    if (!isOpen() || e.key !== 'Tab') return
    const list = listPanelFocusables(panel)
    if (list.length < 2) return
    const target = e.composedPath()[0]
    if (!(target instanceof HTMLElement)) return
    const i = list.indexOf(target)
    if (i === -1) return
    if (!e.shiftKey && i === list.length - 1) {
      e.preventDefault()
      list[0]?.focus()
    } else if (e.shiftKey && i === 0) {
      e.preventDefault()
      list[list.length - 1]?.focus()
    }
  }
  panel.addEventListener('keydown', onPanelKeydown, true)

  // ---- Into the page
  ;(options.target ?? document.body).appendChild(hostEl)

  // A first visit with a widget id waits briefly for the config so the
  // launcher doesn't paint in the wrong colour and then change.
  const waitForConfig = !!widgetId && !cached && !preview
  if (waitForConfig) launcher.style.visibility = 'hidden'
  const revealLauncher = (): void => {
    if (launcher.style.visibility !== 'hidden') return
    launcher.style.visibility = ''
    animateLauncherIn(launcher)
  }
  const revealTimer = waitForConfig ? window.setTimeout(revealLauncher, CONFIG_WAIT_MS) : 0
  if (!waitForConfig) animateLauncherIn(launcher)

  // ---- Studio preview
  const previewOrigins = preview
    ? resolvePreviewOrigins(host.previewOrigins, typeof document !== 'undefined' ? document.referrer : null)
    : []

  const handlePreviewMessage = (message: PreviewInboundMessage): void => {
    const state = store.get()
    const p = state.preview ?? { state: 'closed' as const, highlight: null, demoRun: 0 }
    switch (message.type) {
      case 'aevo-echo:preview-config': {
        const resolved = resolveWidget(
          { status: 'ok', widgetId: null, config: message.config, announcement: null, suggestedPrompts: [], away: false },
          undefined,
          null,
        )
        store.set({
          config: resolved.config,
          legacy: false,
          announcement: resolved.announcement,
          conversationTtlMs: resolved.conversationTtlMs,
        })
        return
      }
      case 'aevo-echo:preview-state':
        store.set({ preview: { ...p, state: message.state } })
        if (message.state === 'closed') controller.close()
        else controller.open()
        return
      case 'aevo-echo:preview-highlight':
        store.set({ preview: { ...p, highlight: message.part } })
        if (message.part === 'launcher') launcher.setAttribute('data-se-highlight', '')
        else launcher.removeAttribute('data-se-highlight')
        return
      case 'aevo-echo:preview-demo':
        store.set({ preview: { ...p, state: 'conversation', demoRun: p.demoRun + 1 } })
        controller.open()
        return
    }
  }

  if (preview) {
    preview_ = attachPreview({ origins: previewOrigins, shadowRoot, onMessage: handlePreviewMessage })
    void ensurePanel()
  } else {
    // ---- Echo's widget config
    void fetchRemote(serverUrl, widgetId).then((remote) => {
      if (destroyed) return
      if (remote.status === 'origin-not-allowed') {
        // The business hasn't listed this website for the widget: show nothing.
        console.warn(`[aevo-echo] this website is not allowed for widget ${widgetId ?? ''}; add it in the widget studio`)
        if (widgetId) writeCachedConfig(widgetId, null)
        mounted?.destroy()
        return
      }
      if (widgetId && remote.status === 'ok') writeCachedConfig(widgetId, remote.config)
      const resolved = resolveWidget(remote, host, inline)
      store.set({
        config: resolved.config,
        legacy: resolved.legacy,
        announcement: resolved.announcement,
        away: resolved.away,
        conversationTtlMs: resolved.conversationTtlMs,
        widgetId: remote.widgetId,
      })
      window.clearTimeout(revealTimer)
      revealLauncher()
    })
  }

  mounted = {
    hostEl,
    shadowRoot,
    refs,
    store,
    open: controller.open,
    close: controller.close,
    toggle: controller.toggle,
    isOpen,
    update(patch: unknown) {
      if (!patch || typeof patch !== 'object') return
      const config = normalizeWidgetConfig(patch, store.get().config)
      const setsAnnouncement =
        typeof (patch as { content?: { welcome?: { announcement?: { text?: unknown } } } }).content?.welcome
          ?.announcement?.text === 'string'
      store.set({
        config,
        legacy: false,
        announcement: setsAnnouncement ? config.content.welcome.announcement.text : store.get().announcement,
      })
    },
    destroy() {
      destroyed = true
      window.clearTimeout(revealTimer)
      unsubscribeConfig()
      preview_?.dispose()
      panelHandle?.destroy()
      panelHandle = null
      document.removeEventListener('keydown', onDocumentKeydown)
      panel.removeEventListener('keydown', onPanelKeydown, true)
      disposePosition()
      resize.dispose()
      hostEl.remove()
      mounted = null
    },
    panelReady: () => ensurePanel(),
  }
  return mounted
}
